import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPKR } from './currencyFormatter';
import { addPdfBrandLogo } from './pdfLogo';
import { getSaleItemProductCode } from './saleInvoiceDisplay';

function pick(obj, snake, camel, fallback = null) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (obj[snake] != null) return obj[snake];
  if (obj[camel] != null) return obj[camel];
  return fallback;
}

function variantLabel(item) {
  const variant = item.variant || {};
  const label = [variant.color, variant.size]
    .filter(Boolean)
    .filter((part) => String(part).toLowerCase() !== 'default')
    .join(' / ');
  return label || '—';
}

/**
 * @param {{ businessName: string, sale: object }} options
 */
export async function downloadSaleInvoicePdf({ businessName, sale }) {
  const s = sale || {};
  const items = Array.isArray(s.items) ? s.items : [];
  const invoiceNumber = pick(s, 'invoice_number', 'invoiceNumber', '—');
  const saleDate = pick(s, 'sale_date', 'saleDate', '—');
  const branchName = s.branch?.name || '—';
  const customerName = s.customer?.name || s.customer_name || s.customerName || 'Walk-in';
  const customerPhone = s.customer?.phone || s.customer_phone || s.customerPhone || '—';
  const status = String(pick(s, 'status', 'status', '') || '').toUpperCase() || '—';

  const subtotal = parseFloat(pick(s, 'subtotal', 'subtotal', 0)) || 0;
  const discount = parseFloat(pick(s, 'discount', 'discount', 0)) || 0;
  const tax = parseFloat(pick(s, 'tax', 'tax', 0)) || 0;
  const totalAmount = parseFloat(pick(s, 'total_amount', 'totalAmount', 0)) || 0;
  const paidAmount = parseFloat(pick(s, 'paid_amount', 'paidAmount', 0)) || 0;
  const balance = Math.max(0, totalAmount - paidAmount);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 30, startY: 8, gapAfterMm: 3 });
  let y = nextY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Invoice', margin, y);
  doc.setFontSize(11);
  doc.text(String(invoiceNumber), margin + 28, y);
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(String(businessName || 'Business').trim(), margin, y);
  y += 5;
  doc.text(`Branch: ${branchName}`, margin, y);
  y += 5;
  doc.text(`Date: ${saleDate}`, margin, y);
  y += 5;
  doc.text(`Customer: ${customerName}`, margin, y);
  y += 5;
  doc.text(`Phone: ${customerPhone}`, margin, y);
  y += 5;
  doc.text(`Status: ${status}`, margin, y);
  y += 6;

  const body = items.map((item) => {
    const qty = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(pick(item, 'unit_price', 'unitPrice', 0)) || 0;
    const lineDiscount = parseFloat(item.discount) || 0;
    const lineTotal = parseFloat(pick(item, 'total_price', 'totalPrice', qty * unitPrice - lineDiscount)) || 0;
    const productCode = getSaleItemProductCode(item);
    const productName = item.product?.name || '—';
    return [
      String(productCode).substring(0, 14),
      String(productName).substring(0, 32),
      variantLabel(item).substring(0, 18),
      String(qty),
      formatPKR(unitPrice),
      formatPKR(lineDiscount),
      formatPKR(lineTotal),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['Product code', 'Product', 'Variant', 'Qty', 'Unit price', 'Discount', 'Total']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [55, 65, 81], textColor: [255, 255, 255], fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 2 },
    margin: { left: margin, right: margin },
    tableWidth: pageW - margin * 2,
    columnStyles: {
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
  });

  let summaryY = (doc.lastAutoTable?.finalY || y + 40) + 8;
  const summaryX = pageW - margin - 70;
  const summaryLines = [
    ['Subtotal:', formatPKR(subtotal)],
    ['Discount:', formatPKR(discount)],
    ['Tax:', formatPKR(tax)],
    ['Total:', formatPKR(totalAmount)],
    ['Paid:', formatPKR(paidAmount)],
    ['Balance:', formatPKR(balance)],
  ];

  doc.setFontSize(10);
  summaryLines.forEach(([label, value], index) => {
    const isTotal = label === 'Total:';
    doc.setFont('helvetica', isTotal ? 'bold' : 'normal');
    doc.text(label, summaryX, summaryY);
    doc.text(value, pageW - margin, summaryY, { align: 'right' });
    summaryY += isTotal ? 6 : 5;
  });

  const safeInvoice = String(invoiceNumber).replace(/[^\w-]/g, '-').slice(0, 40);
  doc.save(`invoice-${safeInvoice}.pdf`);
}
